
import { IntlShape } from 'react-intl';

/** What the dialog is being opened for. */
export type WordingFlavor = 'select' | 'change';

const articleFor = (noun: string): string => {

    const first = noun.trim().charAt(0).toLowerCase();
    return 'aeiou'.includes(first) ? 'an' : 'a';

};

/** Partner-type singular noun for a report ScopeId. */
const partnerNounForScopeId = (intl: IntlShape, scopeId: number | null | undefined): string => {

    switch (scopeId) {

        case 2: return intl.formatMessage({ id: 'distributor.8955b2a4', defaultMessage: 'Distributor' });
        case 3: return intl.formatMessage({ id: 'reseller.8d01b60c', defaultMessage: 'Reseller' });
        case 4: return intl.formatMessage({ id: 'testing_center.fda9aa47', defaultMessage: 'Testing Center' });
        default: return intl.formatMessage({ id: 'partner.cb022868', defaultMessage: 'Partner' });

    }

};

/**
 * Wording for the partner scope-picker on the Reports page (always single-select).
 *
 * @param intl    IntlShape from useIntl().
 * @param scopeId Report's ScopeId - determines partner-type noun.
 * @param flavor  'select' when the user has no scope yet, 'change' when reopening from the scope banner.
 */
export const partnerScopePickerWording = (intl: IntlShape, scopeId: number, flavor: WordingFlavor): {
    customSuccessButtonText: string;
    dialogCustomTitle: string;
} => {

    const noun = partnerNounForScopeId(intl, scopeId);

    if (flavor === 'change') {

        return {
            customSuccessButtonText: intl.formatMessage({ id: 'change_target.21f75066', defaultMessage: 'Change {target}' }, { target: noun }),
            dialogCustomTitle: intl.formatMessage({ id: 'change_target.21f75066', defaultMessage: 'Change {target}' }, { target: noun })
        };

    }

    return {
        customSuccessButtonText: intl.formatMessage({ id: 'select_target.0903fa8f', defaultMessage: 'Select {target}' }, { target: noun }),
        dialogCustomTitle: intl.formatMessage({ id: 'select_target.0903fa8f', defaultMessage: 'Select {target}' }, { target: `${articleFor(noun)} ${noun}` })
    };

};

/** Wording for the student scope-picker on the Reports page (always single-select). */
export const studentScopePickerWording = (intl: IntlShape, flavor: WordingFlavor): {
    customSuccessButtonText: string;
    dialogCustomTitle: string;
} => {

    const noun = intl.formatMessage({ id: 'student.4f46d2e3', defaultMessage: 'Student' });

    if (flavor === 'change') {

        return {
            customSuccessButtonText: intl.formatMessage({ id: 'change_target.21f75066', defaultMessage: 'Change {target}' }, { target: noun }),
            dialogCustomTitle: intl.formatMessage({ id: 'change_target.21f75066', defaultMessage: 'Change {target}' }, { target: noun })
        };

    }

    return {
        customSuccessButtonText: intl.formatMessage({ id: 'select_target.0903fa8f', defaultMessage: 'Select {target}' }, { target: noun }),
        dialogCustomTitle: intl.formatMessage({ id: 'select_target.0903fa8f', defaultMessage: 'Select {target}' }, { target: `${articleFor(noun)} ${noun}` })
    };

};

/**
 * Wording for the partner picker when opened as a Reports-page *filter*.
 * `typeScopeId` comes from the filter's `Type` field (parsed int).
 */
export const partnerFilterPickerWording = (intl: IntlShape, typeScopeId: number, multiSelect: boolean): {
    customSuccessButtonText: string;
    dialogCustomTitle: string;
} => {

    const singular = partnerNounForScopeId(intl, typeScopeId);

    if (multiSelect) {

        const plural = `${singular}s`;
        return {
            customSuccessButtonText: intl.formatMessage({ id: 'select_target.0903fa8f', defaultMessage: 'Select {target}' }, { target: plural }),
            dialogCustomTitle: intl.formatMessage({ id: 'select_target.0903fa8f', defaultMessage: 'Select {target}' }, { target: plural })
        };

    }

    return {
        customSuccessButtonText: intl.formatMessage({ id: 'select_target.0903fa8f', defaultMessage: 'Select {target}' }, { target: singular }),
        dialogCustomTitle: intl.formatMessage({ id: 'select_target.0903fa8f', defaultMessage: 'Select {target}' }, { target: `${articleFor(singular)} ${singular}` })
    };

};

/** Wording for the access-code (transaction) picker when opened as a Reports-page filter. */
export const transactionFilterPickerWording = (intl: IntlShape, multiSelect: boolean): {
    actionButtonText: string;
    customDialogLabel: string;
} => {

    if (multiSelect) {

        const plural = intl.formatMessage({ id: 'access_codes.3114ae04', defaultMessage: 'Access Codes' });
        return {
            actionButtonText: intl.formatMessage({ id: 'select_target.0903fa8f', defaultMessage: 'Select {target}' }, { target: plural }),
            customDialogLabel: intl.formatMessage({ id: 'select_target.0903fa8f', defaultMessage: 'Select {target}' }, { target: plural })
        };

    }

    const singular = intl.formatMessage({ id: 'access_code.b5fc1b57', defaultMessage: 'Access Code' });
    return {
        actionButtonText: intl.formatMessage({ id: 'select_target.0903fa8f', defaultMessage: 'Select {target}' }, { target: singular }),
        customDialogLabel: intl.formatMessage({ id: 'select_target.0903fa8f', defaultMessage: 'Select {target}' }, { target: `${articleFor(singular)} ${singular}` })
    };

};
